import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import {
  Check as ApproveIcon,
  Send as PostIcon,
  Block as VoidIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import { useAccessControl } from '../../../hooks/useAccessControl';
import { RESOURCES, ACTIONS } from '../../../hooks/usePermissions';
import { ledgerAPI } from '../../../services/api';
import { formatAccountingAmount } from '../../../utils/formatters';
import { useAppSnackbar, useConfirmDialog } from '../../common/TableDesign';
import LedgerListPage from '../LedgerListPage';
import LedgerFormDialog from '../LedgerFormDialog';
import { BankAccountPicker, LedgerPicker } from '../LedgerPickers';
import { ExpenseStatusChip, fmtDate, getErrorMessage, unwrap, unwrapList } from './ExpensesShared';

const STATUSES = ['DRAFT', 'APPROVED', 'POSTED', 'VOID'];

const emptyForm = () => ({
  claim_id: '', reimbursement_date: new Date().toISOString().slice(0, 10),
  amount: '', paid_from_account_id: '',
});

// Reimbursements pay out an APPROVED expense claim - this is what actually
// posts to the GL (the claim itself is just an approval record). DRAFT ->
// APPROVED -> POSTED; approve/void run as checker, create/post as maker -
// same lifecycle as receivables/PaymentsTab.js, this module's closest
// verified analog.
const ExpenseReimbursementsTab = () => {
  const { can } = useAccessControl();
  const canCreate = can(RESOURCES.LEDGER.EXPENSES, ACTIONS.CREATE);
  const canApprove = can(RESOURCES.LEDGER.EXPENSES, ACTIONS.APPROVE);
  const canDelete = can(RESOURCES.LEDGER.EXPENSES, ACTIONS.DELETE);
  const { showSnackbar, SnackbarComponent } = useAppSnackbar();
  const { confirm, ConfirmDialogComponent } = useConfirmDialog();

  const [refreshKey, setRefreshKey] = useState(0);
  const [showCreate, setShowCreate] = useState(false);
  const [values, setValues] = useState(emptyForm());
  const [approvedClaims, setApprovedClaims] = useState([]);
  const [loadingClaims, setLoadingClaims] = useState(false);
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null);
  const [voidDlg, setVoidDlg] = useState({ open: false, item: null, void_reason: '' });

  const reload = () => setRefreshKey((k) => k + 1);

  useEffect(() => {
    if (!showCreate) return;
    let alive = true;
    setLoadingClaims(true);
    ledgerAPI.getExpenseClaims({ status: 'APPROVED', limit: 100 })
      .then((res) => { if (alive) setApprovedClaims(unwrapList(res)); })
      .catch(() => { if (alive) setApprovedClaims([]); })
      .finally(() => { if (alive) setLoadingClaims(false); });
    return () => { alive = false; };
  }, [showCreate]);

  const claimOptions = approvedClaims.map((c) => ({
    value: c.claim_id,
    label: `${c.claim_number || `CLM-${c.claim_id}`} - ${c.employee?.name || `Employee ${c.employee_id}`} - ${formatAccountingAmount(c.total_amount)}`,
  }));

  const selectClaim = (claimId) => {
    const claim = approvedClaims.find((c) => String(c.claim_id) === String(claimId));
    setValues((p) => ({ ...p, claim_id: claimId, amount: claim ? String(claim.total_amount) : p.amount }));
  };

  const submitCreate = async () => {
    if (!values.claim_id) {
      showSnackbar('Select the claim being reimbursed', 'error');
      return;
    }
    if (!(Number(values.amount) > 0)) {
      showSnackbar('Amount must be greater than zero', 'error');
      return;
    }
    if (!values.paid_from_account_id) {
      showSnackbar('Bank/cash account is required', 'error');
      return;
    }
    const payload = {
      claim_id: Number(values.claim_id),
      reimbursement_date: values.reimbursement_date,
      amount: Number(values.amount),
      paid_from_account_id: Number(values.paid_from_account_id),
    };
    try {
      setSaving(true);
      await ledgerAPI.createExpenseReimbursement(payload);
      showSnackbar('Reimbursement created as DRAFT', 'success');
      setShowCreate(false);
      setValues(emptyForm());
      reload();
    } catch (error) {
      showSnackbar(getErrorMessage(error), 'error');
    } finally {
      setSaving(false);
    }
  };

  const runWorkflow = (label, pastTense, fn) => {
    confirm({
      title: label,
      message: `Are you sure you want to ${label.toLowerCase()} this reimbursement?`,
      onConfirm: async () => {
        try {
          await fn();
          showSnackbar(`Reimbursement ${pastTense} successfully`, 'success');
          reload();
        } catch (error) {
          showSnackbar(getErrorMessage(error), 'error');
        }
      },
    });
  };

  const openDetail = async (row) => {
    try {
      const res = await ledgerAPI.getExpenseReimbursement(row.reimbursement_id);
      setDetail(unwrap(res));
    } catch (error) {
      showSnackbar(getErrorMessage(error, 'Failed to fetch reimbursement'), 'error');
    }
  };

  const submitVoid = async () => {
    if ((voidDlg.void_reason?.trim() ?? '').length < 5) {
      showSnackbar('Void reason (min 5 chars) is required', 'error');
      return;
    }
    try {
      await ledgerAPI.voidExpenseReimbursement(voidDlg.item.reimbursement_id, { void_reason: voidDlg.void_reason.trim() });
      showSnackbar('Reimbursement voided successfully', 'success');
      setVoidDlg({ open: false, item: null, void_reason: '' });
      setDetail(null);
      reload();
    } catch (error) {
      showSnackbar(getErrorMessage(error), 'error');
    }
  };

  return (
    <>
      <LedgerListPage
        resource={RESOURCES.LEDGER.EXPENSES}
        title="Reimbursements"
        subtitle="Payouts against approved expense claims"
        addLabel="New Reimbursement"
        idKey="reimbursement_id"
        searchPlaceholder="Search reimbursement #..."
        emptyMessage="No reimbursements found."
        refreshKey={refreshKey}
        fetchItems={(params) => ledgerAPI.getExpenseReimbursements(params)}
        onAdd={canCreate ? () => { setValues(emptyForm()); setShowCreate(true); } : null}
        filters={[
          { key: 'status', label: 'Status', options: STATUSES.map((s) => ({ label: s, value: s })) },
          { type: 'daterange', keyFrom: 'start_date', keyTo: 'end_date', label: 'Reimbursement date' },
          { type: 'numberrange', keyFrom: 'min_amount', keyTo: 'max_amount', label: 'amount' },
        ]}
        columns={[
          { label: 'Reimbursement #', render: (r) => <strong>{r.reimbursement_number || `RB-${r.reimbursement_id}`}</strong> },
          { label: 'Claim', render: (r) => r.claim?.claim_number || `CLM-${r.claim_id}` },
          { label: 'Date', render: (r) => fmtDate(r.reimbursement_date) },
          { label: 'Amount', render: (r) => formatAccountingAmount(r.amount), align: 'right' },
          { label: 'Status', render: (r) => <ExpenseStatusChip status={r.status} /> },
        ]}
        rowActions={(r) => {
          const actions = [{ icon: ViewIcon, title: 'View detail', color: 'default', onClick: () => openDetail(r) }];
          if (r.status === 'DRAFT' && canApprove) {
            actions.push({ icon: ApproveIcon, title: 'Approve', color: 'primary', onClick: () => runWorkflow('Approve', 'approved', () => ledgerAPI.approveExpenseReimbursement(r.reimbursement_id)) });
          }
          if (r.status === 'APPROVED' && canApprove) {
            actions.push({ icon: PostIcon, title: 'Post', color: 'primary', onClick: () => runWorkflow('Post', 'posted', () => ledgerAPI.postExpenseReimbursement(r.reimbursement_id)) });
          }
          if (['DRAFT', 'APPROVED', 'POSTED'].includes(r.status) && canApprove) {
            actions.push({ icon: VoidIcon, title: 'Void', color: 'error', onClick: () => setVoidDlg({ open: true, item: r, void_reason: '' }) });
          }
          if (r.status === 'DRAFT' && canDelete) {
            actions.push({
              icon: DeleteIcon, title: 'Delete draft', color: 'error',
              onClick: () => confirm({
                title: 'Delete reimbursement',
                message: `Delete DRAFT reimbursement ${r.reimbursement_number || `RB-${r.reimbursement_id}`}? This cannot be undone.`,
                onConfirm: async () => {
                  try {
                    await ledgerAPI.deleteExpenseReimbursement(r.reimbursement_id);
                    showSnackbar('Reimbursement deleted', 'success');
                    reload();
                  } catch (error) {
                    showSnackbar(getErrorMessage(error), 'error');
                  }
                },
              }),
            });
          }
          return actions;
        }}
      />

      <Dialog open={showCreate} onClose={() => setShowCreate(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New Reimbursement</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ pt: 1 }}>
            <Grid size={12}>
              {loadingClaims ? (
                <Typography variant="body2" color="text.secondary">Loading approved claims...</Typography>
              ) : claimOptions.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No approved claims awaiting reimbursement.</Typography>
              ) : (
                <LedgerPicker label="Approved claim" value={values.claim_id} onChange={selectClaim} options={claimOptions} required />
              )}
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth size="small" type="date" label="Reimbursement Date" value={values.reimbursement_date}
                onChange={(e) => setValues((p) => ({ ...p, reimbursement_date: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid size={12}>
              <TextField
                fullWidth size="small" type="number" label="Amount" value={values.amount}
                onChange={(e) => setValues((p) => ({ ...p, amount: e.target.value }))}
                inputProps={{ min: 0.01, step: 'any' }} required
              />
            </Grid>
            <Grid size={12}>
              <BankAccountPicker label="Paid From" value={values.paid_from_account_id} onChange={(id) => setValues((p) => ({ ...p, paid_from_account_id: id }))} required />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCreate(false)}>Cancel</Button>
          <Button variant="contained" onClick={submitCreate} disabled={saving}>
            {saving ? 'Saving...' : 'Create Draft'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Reimbursement {detail?.reimbursement_number || (detail?.reimbursement_id ? `RB-${detail.reimbursement_id}` : '')}</DialogTitle>
        <DialogContent>
          {detail && (
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>Claim: {detail.claim?.claim_number || `CLM-${detail.claim_id}`}</Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>Date: {fmtDate(detail.reimbursement_date)}</Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>Amount: <strong>{formatAccountingAmount(detail.amount)}</strong></Typography>
              <ExpenseStatusChip status={detail.status} />
              {detail.status === 'VOID' && detail.void_reason && (
                <Typography variant="body2" color="error.main" sx={{ mt: 1 }}>Voided: {detail.void_reason}</Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetail(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <LedgerFormDialog
        open={voidDlg.open}
        onClose={() => setVoidDlg({ open: false, item: null, void_reason: '' })}
        title={`Void reimbursement ${voidDlg.item?.reimbursement_number || (voidDlg.item ? `RB-${voidDlg.item.reimbursement_id}` : '')}`}
        onSubmit={submitVoid}
        values={voidDlg}
        onChange={(key, value) => setVoidDlg((p) => ({ ...p, [key]: value }))}
        maxWidth="sm"
        fields={[{ key: 'void_reason', label: 'Void Reason (min 5 chars)', type: 'textarea', required: true, md: 12 }]}
      />

      {ConfirmDialogComponent}
      {SnackbarComponent}
    </>
  );
};

export default ExpenseReimbursementsTab;
